/**
 * Vendor Auth Context — completely separate from staff AuthContext.
 * Stores the vendor JWT in localStorage under 'vendor_token'.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const VendorAuthContext = createContext(null);

const VENDOR_TOKEN_KEY = 'vendor_token';
const VENDOR_USER_KEY = 'vendor_user';
const API = process.env.REACT_APP_BACKEND_URL;

async function vendorFetch(path, options = {}) {
  const token = localStorage.getItem(VENDOR_TOKEN_KEY);
  const r = await fetch(`${API}/api${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(data.detail || r.statusText), { response: { data } });
  return data;
}

export function VendorAuthProvider({ children }) {
  const [vendorUser, setVendorUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(VENDOR_USER_KEY) || 'null'); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  const login = useCallback(async (email, password) => {
    const data = await vendorFetch('/partner/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    localStorage.setItem(VENDOR_TOKEN_KEY, data.access_token);
    localStorage.setItem(VENDOR_USER_KEY, JSON.stringify(data.vendor_user));
    setVendorUser(data.vendor_user);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(VENDOR_TOKEN_KEY);
    localStorage.removeItem(VENDOR_USER_KEY);
    setVendorUser(null);
    window.location.href = '/partner/login';
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const d = await vendorFetch('/partner/auth/me');
      const u = d.vendor_user || d;
      localStorage.setItem(VENDOR_USER_KEY, JSON.stringify(u));
      setVendorUser(u);
    } catch { logout(); }
  }, [logout]);

  return (
    <VendorAuthContext.Provider value={{ vendorUser, loading, login, logout, refreshUser, vendorFetch }}>
      {children}
    </VendorAuthContext.Provider>
  );
}

export function useVendorAuth() { return useContext(VendorAuthContext); }

export { vendorFetch };
